/**
 * ClientPortalProject — public, view-only project page for external
 * clients. Opened via a shareable, expiring token URL:
 *   /klient/p/:token
 *
 * All UI text is Norwegian (this page is only ever shown to Norwegian
 * clients — internal Doserne workers use `/doserne/prosjekter/:id`).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, FileText, ClipboardList, ShieldAlert, Camera, Calendar,
  MapPin, User as UserIcon, CheckCircle2, AlertCircle, ArrowRight, Clock,
  Building2, ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    return d.toLocaleDateString("no-NO", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function fmtDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    return d.toLocaleDateString("no-NO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("no-NO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function ErrorPage({ title, hint }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md text-center shadow-md">
        <div className="w-14 h-14 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-red-500" />
        </div>
        <h1 className="text-xl font-black text-slate-900 mb-2">{title}</h1>
        {hint && <p className="text-sm text-slate-600">{hint}</p>}
      </div>
    </div>
  );
}

function SectionHeader({ Icon, title, count, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    orange: "bg-orange-100 text-orange-700",
    cyan: "bg-cyan-100 text-cyan-700",
  };
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone] || tones.slate}`}>
        <Icon className="w-4 h-4" />
      </div>
      <h2 className="text-lg font-black text-slate-900">{title}</h2>
      {count > 0 && (
        <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
          {count}
        </span>
      )}
    </div>
  );
}

function KV({ Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className="text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function UnderlagRow({ u }) {
  const isSigned = u.status === "signed";
  const signUrl = u.sign_token ? `/underlag/sign/${u.sign_token}` : (u.slug ? `/u/${u.slug}` : null);
  return (
    <div
      className={`bg-white border-2 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${isSigned ? "border-slate-200" : "border-amber-300 shadow-sm shadow-amber-100"}`}
      data-testid={`portal-underlag-${u.underlag_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {isSigned ? (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-1.5 py-0.5">
              <CheckCircle2 className="w-3 h-3" /> Signert
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-0.5">
              <Clock className="w-3 h-3" /> Ikke signert
            </span>
          )}
          {u.number && <span className="text-xs font-mono text-slate-500">#{u.number}</span>}
          <span className="text-xs text-slate-500">·</span>
          <span className="text-xs font-semibold text-slate-700">{fmtDate(u.date)}</span>
        </div>
        <div className="mt-1 text-sm font-black text-slate-900 truncate">
          {u.work_description || u.address || "Underlag"}
        </div>
        {u.address && u.address !== u.work_description && (
          <div className="text-xs text-slate-500 truncate">{u.address}</div>
        )}
        {isSigned && u.signed_at && (
          <div className="text-[11px] text-emerald-700 mt-0.5">Signert {fmtDateTime(u.signed_at)}</div>
        )}
      </div>
      {signUrl && !isSigned && (
        <a
          href={signUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-sm font-black self-stretch sm:self-auto justify-center"
          style={{ color: "#fff" }}
          data-testid={`portal-sign-btn-${u.underlag_id}`}
        >
          Signer <ArrowRight className="w-4 h-4" />
        </a>
      )}
      {isSigned && signUrl && (
        <a
          href={signUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-semibold self-stretch sm:self-auto justify-center"
        >
          Se dokument
        </a>
      )}
    </div>
  );
}

function ProgressDayCard({ day, posts, onOpenPhoto }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`portal-progress-day-${day}`}>
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-bold text-slate-900 capitalize">{fmtDay(day)}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {posts.length} innlegg
        </div>
      </div>
      <div className="p-3 sm:p-4 space-y-4">
        {posts.map((post) => (
          <div key={post.id} className="border border-slate-200 rounded-xl p-3" data-testid={`portal-progress-post-${post.id}`}>
            <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
              <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-[10px] font-bold" style={{ color: "#fff" }}>
                {(post.author || "?")[0]?.toUpperCase()}
              </div>
              <span className="font-semibold text-slate-900">{post.author || "Doserne"}</span>
            </div>
            {post.note && <p className="text-sm text-slate-700 mb-2 whitespace-pre-wrap">{post.note}</p>}
            {post.photos && post.photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                {post.photos.map((ph, i) => (
                  <button
                    key={i}
                    onClick={() => onOpenPhoto({ url: ph.url, caption: post.note })}
                    className="aspect-square bg-slate-100 rounded-lg overflow-hidden hover:opacity-90 active:opacity-70"
                    data-testid={`portal-photo-${post.id}-${i}`}
                  >
                    <img src={ph.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClientPortalProject() {
  const { token, customerToken, projectId } = useParams();
  // Two entry points supported:
  //   /klient/p/:token                       — per-project token
  //   /klient/c/:customerToken/p/:projectId — customer token scoping this project
  const apiUrl = customerToken
    ? `${API}/client-portal/customer/${customerToken}/projects/${projectId}`
    : `${API}/client-portal/${token}`;
  const backLink = customerToken ? `/klient/c/${customerToken}` : null;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(apiUrl);
        setData(data);
      } catch (e) {
        if (e.response?.status === 410) setError({ title: "Lenken er utløpt eller tilbakekalt", hint: "Kontakt oss for en ny lenke." });
        else if (e.response?.status === 404) setError({ title: "Ugyldig lenke", hint: "Sjekk URL-en du fikk tilsendt." });
        else if (e.response?.status === 403) setError({ title: "Ingen tilgang", hint: "Dette prosjektet tilhører ikke kunden i denne lenken." });
        else setError({ title: "Kunne ikke laste inn", hint: e.response?.data?.detail || "Prøv igjen litt senere." });
      }
      setLoading(false);
    })();
  }, [apiUrl]);

  const unsignedFirst = useMemo(() => {
    if (!data?.underlags) return { unsigned: [], signed: [] };
    return {
      unsigned: data.underlags.filter((u) => u.status !== "signed"),
      signed: data.underlags.filter((u) => u.status === "signed"),
    };
  }, [data]);

  // Group progress posts by day (YYYY-MM-DD), newest first
  const progressByDay = useMemo(() => {
    if (!data?.progress) return [];
    const groups = new Map();
    for (const p of data.progress) {
      const day = (p.date || "").slice(0, 10) || "ukjent";
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day).push(p);
    }
    return Array.from(groups.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([day, posts]) => ({ day, posts }));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error) return <ErrorPage title={error.title} hint={error.hint} />;
  if (!data) return null;

  const { project, counts, kontrolls, sjas, expires_at } = data;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="client-portal-root">
      {/* Dark hero */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
          {backLink && (
            <Link
              to={backLink}
              className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white mb-3 font-semibold"
              data-testid="portal-back-to-customer"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Tilbake til alle prosjekter
            </Link>
          )}
          <div className="text-[10px] uppercase tracking-widest font-black text-cyan-300 mb-1">Kundeportal · Doserne Utleie AS</div>
          <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight" data-testid="portal-project-title">
            {project.address || project.name || "Prosjekt"}
          </h1>
          {project.name && project.name !== project.address && (
            <div className="text-sm text-slate-300 mt-1">{project.name}</div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-300">
            {project.number && <span className="font-mono uppercase">#{project.number}</span>}
            {project.customer_name && (
              <span className="inline-flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" /> {project.customer_name}</span>
            )}
            {project.start_date && (
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(project.start_date)}{project.end_date ? ` → ${fmtDate(project.end_date)}` : ""}</span>
            )}
            {project.address && (
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {project.address}</span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-4 space-y-8">
        {/* Info card — mirrors the admin project detail top card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-3">Detaljer</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
            <KV Icon={UserIcon} label="Kunde" value={project.customer_name} />
            <KV Icon={MapPin}   label="Adresse" value={project.address} />
            <KV Icon={Building2} label="Prosjektnummer" value={project.number} />
            <KV Icon={Calendar} label="Start" value={fmtDate(project.start_date)} />
            <KV Icon={Calendar} label="Slutt" value={fmtDate(project.end_date)} />
          </div>
          {project.description && (
            <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-700 whitespace-pre-wrap">
              {project.description}
            </div>
          )}
          {/* Counters row — same visual style as admin */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-5 pt-5 border-t border-slate-100">
            {[
              { label: "Ikke signert", value: counts.underlags_unsigned, color: "text-amber-600" },
              { label: "Underlag",     value: counts.underlags,          color: "text-slate-700" },
              { label: "Kontroll",     value: counts.kontrolls,          color: "text-blue-600" },
              { label: "SJA",          value: counts.sjas,               color: "text-orange-600" },
              { label: "Progresjon",   value: counts.progress_posts,     color: "text-cyan-600" },
            ].map((c) => (
              <div key={c.label} className="text-center" data-testid={`portal-count-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className={`text-2xl font-black ${c.color}`}>{c.value ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Underlag */}
        <section data-testid="portal-underlags-section">
          <SectionHeader
            Icon={FileText}
            title="Underlag / Timesheet"
            count={counts.underlags}
            tone={unsignedFirst.unsigned.length > 0 ? "amber" : "slate"}
          />
          {counts.underlags === 0 ? (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500 italic">
              Ingen underlag for dette prosjektet enda.
            </div>
          ) : (
            <div className="space-y-2">
              {unsignedFirst.unsigned.length > 0 && (
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-700 mb-1">
                  Venter på din signatur ({unsignedFirst.unsigned.length})
                </div>
              )}
              {unsignedFirst.unsigned.map((u) => <UnderlagRow key={u.underlag_id} u={u} />)}
              {unsignedFirst.signed.length > 0 && unsignedFirst.unsigned.length > 0 && (
                <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mt-4 mb-1">
                  Signert ({unsignedFirst.signed.length})
                </div>
              )}
              {unsignedFirst.signed.map((u) => <UnderlagRow key={u.underlag_id} u={u} />)}
            </div>
          )}
        </section>

        {/* Progresjon — grouped by day, mirrors admin timeline */}
        <section data-testid="portal-progress-section">
          <SectionHeader Icon={Camera} title="Progresjon" count={counts.progress_posts} tone="cyan" />
          {progressByDay.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <div className="text-sm text-slate-500">Ingen progresjon publisert enda.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {progressByDay.map(({ day, posts }) => (
                <ProgressDayCard key={day} day={day} posts={posts} onOpenPhoto={setLightbox} />
              ))}
            </div>
          )}
        </section>

        {/* Kontroll */}
        {kontrolls.length > 0 && (
          <section data-testid="portal-kontroll-section">
            <SectionHeader Icon={ClipboardList} title="Kontroll" count={counts.kontrolls} tone="blue" />
            <div className="space-y-2">
              {kontrolls.map((d) => (
                <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="text-sm font-black text-slate-900">{d.title || "Kontroll"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtDate(d.date)} {d.status && `· ${d.status}`}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SJA */}
        {sjas.length > 0 && (
          <section data-testid="portal-sja-section">
            <SectionHeader Icon={ShieldAlert} title="SJA" count={counts.sjas} tone="orange" />
            <div className="space-y-2">
              {sjas.map((d) => (
                <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="text-sm font-black text-slate-900">{d.title || "SJA"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {fmtDate(d.date)}
                    {d.risk_level && <span className="ml-1">· risikonivå: {d.risk_level}</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Footer */}
        <div className="text-[10px] uppercase tracking-widest text-slate-400 text-center pt-4">
          Lenke gyldig til {fmtDate(expires_at)}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox.url} alt={lightbox.caption || ""} className="max-h-[90vh] max-w-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
